<template>
  <el-dialog
    :model-value="isOpen"
    width="744px"
    @opened="opened"
    @closed="closed"
    @close="$emit('close')"
  >
    <h1 class="tw-mb-8 tw-tracking-wider">
      {{ $t('adm.companies.company_dialog.title_edit') }}
    </h1>
    <div v-loading="!isDataLoaded">
      <el-form
        v-if="isDataLoaded"
        ref="Form"
        :model="form"
        :rules="formRules"
        label-position="top"
        :disabled="isLoading"
        class="tw-flex tw-flex-col tw-gap-2 tw-mt-3"
        @submit.native.prevent="submitForm"
      >
        <div class="tw-flex tw-items-start tw-gap-2">
          <i-form-item
            :label="$t('common.fields.full_name')"
            class="tw-flex-3"
            :class="[
              company.fullname == null ? '' : 'has-value'
            ]"
          >
            <el-input
              disabled
              :value="company.fullname"
            />
          </i-form-item>
          <i-form-item
            :label="$t('adm.companies.company_dialog.ico')"
            class="tw-flex-1"
            :class="[
              company.ic_number == null ? '' : 'has-value'
            ]"
          >
            <el-input
              disabled
              :value="company.ic_number"
            />
          </i-form-item>
        </div>
        <div class="tw-flex tw-items-start tw-gap-2">
          <i-form-item
            :label="$t('adm.companies.company_dialog.street')"
            prop="address_street"
            class="tw-flex-3"
            :error="errors?.address_street?.[0]"
            :class="[
              form.address_street.length === 0 ? '' : 'has-value'
            ]"
          >
            <el-input
              v-model="form.address_street"
            />
          </i-form-item>
          <i-form-item
            :label="$t('adm.companies.company_dialog.house_number')"
            prop="address_house_number"
            class="tw-flex-1"
            :error="errors?.address_house_number?.[0]"
            :class="[
              form.address_house_number.length === 0 ? '' : 'has-value'
            ]"
          >
            <el-input
              v-model="form.address_house_number"
            />
          </i-form-item>
        </div>

        <div class="tw-flex tw-items-start tw-gap-2">
          <i-form-item
            :label="$t('adm.companies.company_dialog.city')"
            prop="address_city"
            :error="errors?.address_city?.[0]"
            :class="[
              form.address_city.length === 0 ? '' : 'has-value'
            ]"
          >
            <el-input
              v-model="form.address_city"
            />
          </i-form-item>

          <i-form-item
            :label="$t('adm.companies.company_dialog.zip')"
            prop="address_zip"
            :error="errors?.address_zip?.[0]"
            :class="[
              form.address_zip.length === 0 ? '' : 'has-value'
            ]"
          >
            <el-input
              v-model="form.address_zip"
            />
          </i-form-item>
        </div>
        <div class="tw-flex">
          <i-form-item
            :label="$t('common.fields.short_name')"
            prop="short_name"
            :error="errors?.short_name?.[0]"
            :class="[
              form.short_name.length === 0 ? '' : 'has-value'
            ]"
          >
            <el-input
              v-model="form.short_name"
            />
          </i-form-item>
        </div>
        <div class="tw-flex">
          <i-form-item
            prop="client_companies"
            :error="errors?.client_companies?.[0]"
          >
            <MultiSelectWithSearch
              v-model="form.client_companies"
              option-object-label-key="name"
              :options="companiesOptions"
              :label="$t('adm.companies.company_dialog.client_companies')"
              :handle-change="(payload) => form.client_companies = payload"
            />
          </i-form-item>
        </div>
        <div class="tw-flex tw-items-start tw-gap-2">
          <i-form-item
            :label="$t('common.fields.phone')"
            prop="contact_phone"
            :error="errors?.contact_phone?.[0]"
            :class="[
              form.contact_phone.length === 0 ? '' : 'has-value'
            ]"
          >
            <el-input
              v-model="form.contact_phone"
              type="tel"
            />
          </i-form-item>
          <i-form-item
            :label="$t('common.fields.email')"
            prop="contact_email"
            :error="errors?.contact_email?.[0]"
            :class="[
              form.contact_email.length === 0 ? '' : 'has-value'
            ]"
          >
            <el-input
              v-model="form.contact_email"
              type="email"
            />
          </i-form-item>
        </div>
        <div class="tw-flex">
          <el-form-item :error="errors?.is_active?.[0]">
            <el-switch
              v-model="form.is_active"
              :active-text="$t('common.fields.active')"
              class="tw-mt-2"
            />
          </el-form-item>
        </div>
      </el-form>
    </div>
    <template #footer>
      <div class="tw-flex tw-justify-end tw-gap-2 flex-col md:flex-row">
        <i-button
          class="tw-w-40"
          @click="$emit('close')"
        >
          {{ $t('common.actions.cancel') }}
        </i-button>
        <i-button
          type="primary"
          class="tw-w-40"
          :is-loading="isLoading"
          :is-disabled="!formHasChanged"
          @click="submitForm"
        >
          {{ $t('common.actions.save') }}
        </i-button>
      </div>
    </template>
  </el-dialog>
</template>

<script>
import { mapGetters } from 'vuex';

import _get from 'lodash/get';
import _isEqual from 'lodash/isEqual';
import { deepDiff } from '@/helpers';
import MultiSelectWithSearch from '@/components/shared/MultiSelectWithSearch.vue';

export default {
  name: 'EditAgencyCompanyDialog',
  components: { MultiSelectWithSearch },
  props: {
    isOpen: {
      type: Boolean,
      default: false,
    },
    companyId: {
      type: Number,
      required: true,
    },
  },

  data() {
    return {
      isLoading: false,
      form: {
        address_street: '',
        address_house_number: '',
        address_city: '',
        address_zip: '',
        short_name: '',
        contact_phone: '',
        contact_email: '',
        is_active: true,
        client_companies: [],
      },
      company: {},
      isDataLoaded: false,
      initialForm: {},
      formRules: {
        address_street: [
          { required: false, message: 'Street address length must be from 3 to 129', trigger: 'blur', min: 3 },
        ],
        address_house_number: [
          { required: false, message: 'House number length must be less than 128', trigger: 'blur', max: 128 },
        ],
        address_city: [
          { required: false, message: 'City length must be less than 128', trigger: 'blur', max: 128 },
        ],
        address_zip: [
          { required: false, message: 'Zip length must be less than 16', trigger: 'blur', max: 16 },
        ],
        short_name: [
          { required: false, message: 'Short name is required', trigger: 'blur' },
        ],
        contact_phone: [
          { required: false, message: 'Phone number length must be less than 32', trigger: 'blur', max: 32 },
        ],
        contact_email: [
          { required: false, message: 'Please enter valid email address', trigger: 'blur', type: 'email' },
        ],
        is_active: [
          { required: true, message: this.$t('common.fields.required'), trigger: 'blur' },
        ],
        client_companies: [
          { required: false, message: 'Client company is not required', trigger: 'blur' },
        ],
      },
      errors: {},
    };
  },

  computed: {
    ...mapGetters({
      companiesOptions: 'v2/admin/cms/clients/companies/selectOptions',
    }),
    formHasChanged() {
      return !_isEqual(this.form, this.initialForm);
    },
  },

  watch: {
    company() {
      this.setInitialFormData();
    },
    async isOpen(val) {
      if (val) {
        this.isDataLoaded = false;
        this.company = await this.$store.dispatch('v2/admin/cms/agency/companies/getCompanyDetails', {
          id: this.companyId,
        });
        this.isDataLoaded = true;
      }
    },
  },

  methods: {
    setInitialFormData() {
      const companyKeys = Object.keys(this.company);
      const formKeys = Object.keys(this.form);

      formKeys.forEach((key) => {
        if (companyKeys.includes(key)) {
          this.form[key] = this.company[key];
          this.initialForm[key] = this.company[key];
        }
      });
      this.$refs.Form?.clearValidate();
    },

    opened() {
      this.$refs.Form?.clearValidate();
    },
    closed() {
      this.$refs.Form?.clearValidate();
    },

    async submitForm() {
      this.errors = {};
      const isValid = await this.$refs.Form.validate().catch(() => false);
      if (!isValid) {
        return;
      }

      this.isLoading = true;

      try {
        const response = await this.$store.dispatch('v2/admin/cms/agency/companies/editCompany', {
          id: this.company.id,
          company: deepDiff(this.initialForm, this.form),
        });

        const message = _get(response, 'data.message');
        this.$message.success({
          message: this.$capitalize(message || 'Success'),
          showClose: true,
        });

        this.$emit('close');
      } catch(error) {
        this.errors = error.response.data.data;

        this.$message.error({
          message: this.$capitalize(error.response.data.message),
          showClose: true,
        });
      }

      this.isLoading = false;
    },
  },
};
</script>
